import { useState } from 'react';
import StyledSvg from '@sdk/components/ui/StyledSvg';
import { useTheme } from 'styled-components';
import gettext from '@sdk/lang/gettext';

import { Article } from '@web/newsfeed/types/Article';
import LikeGraySvg from '@web/assets/svg/Icons/16/thumbup-gray.svg';
import LikeBlueSvg from '@web/assets/svg/Icons/16/thumbup-blue.svg';
import CommentActiveSvg from '@web/assets/svg/Icons/16/comment-blue.svg';
import CommentInactiveSvg from '@web/assets/svg/Icons/16/comment-gray.svg';
import SeenSvg from '@web/assets/svg/Icons/16/seen-gray.svg';
import { SeenByModal } from '@web/newsfeed/SeenBy/SeenByModal';
import { LikedByModal } from '@web/newsfeed/LikedBy/likedByModal';
import {
  FooterText,
  IconWithCounterWrapper,
  LikesAndCommentsWrapper,
} from '@web/newsfeed/Article/article.styles';

import { ArticleFooterWrapper, FooterItem } from './articleFooter.styles';
import { useArticleFooter } from './articleFooter.hooks';

type Props = {
  article: Article;
  onLikeClick: () => void;
  toggleComments: () => void;
};

export const ArticleFooter = ({ article, onLikeClick, toggleComments }: Props) => {
  const theme = useTheme();
  const { likedByMe, likesCount, commentedByMe, commentsCount, seenBy } = article;
  const hasLikes = Boolean(likesCount);
  const hasComments = Boolean(commentsCount);
  const [isSeenByModalVisible, setIsSeenByModalVisible] = useState(false);
  const [isLikedByModalVisible, setIsLikedByModalVisible] = useState(false);
  const { pageMembersCount } = useArticleFooter(article);

  return (
    <ArticleFooterWrapper>
      <LikesAndCommentsWrapper>
        <FooterItem>
          <FooterText clickable highlighted={likedByMe} onClick={onLikeClick}>
            {gettext('Like')}
          </FooterText>
        </FooterItem>

        <FooterItem>
          <FooterText>•</FooterText>
        </FooterItem>

        <FooterItem>
          <FooterText clickable onClick={toggleComments}>
            {gettext('Comment')}
          </FooterText>
        </FooterItem>

        {(hasLikes || hasComments) && <FooterText>•</FooterText>}
        {hasLikes && (
          <FooterText highlighted={likedByMe} clickable>
            <IconWithCounterWrapper
              onClick={(e) => {
                e.stopPropagation();
                setIsLikedByModalVisible(true);
              }}>
              <StyledSvg
                svg={likedByMe ? LikeBlueSvg : LikeGraySvg}
                color={likedByMe ? theme.colors.primary : 'inherit'}></StyledSvg>
              <span>{likesCount}</span>
            </IconWithCounterWrapper>
          </FooterText>
        )}
        {hasLikes && hasComments && <FooterText>•</FooterText>}
        {hasComments && (
          <FooterText clickable>
            <IconWithCounterWrapper
              onClick={(e) => {
                e.stopPropagation();
                toggleComments();
              }}>
              <StyledSvg
                svg={commentedByMe ? CommentActiveSvg : CommentInactiveSvg}
                color={commentedByMe ? theme.colors.primary : 'inherit'}></StyledSvg>
              <span>{commentsCount}</span>
            </IconWithCounterWrapper>
          </FooterText>
        )}
      </LikesAndCommentsWrapper>

      <FooterText clickable>
        <IconWithCounterWrapper
          onClick={(e) => {
            e.stopPropagation();
            setIsSeenByModalVisible(true);
          }}>
          <StyledSvg svg={SeenSvg} color="inherit"></StyledSvg>
          <span>
            {seenBy || 0}
            {pageMembersCount ? ` / ${pageMembersCount}` : ''}
          </span>
        </IconWithCounterWrapper>
      </FooterText>

      {isSeenByModalVisible && (
        <SeenByModal
          isVisible={isSeenByModalVisible}
          onClose={() => setIsSeenByModalVisible(false)}
          pageId={article.page.id}
          articleId={article.id}
          seenByCount={article.seenBy}
        />
      )}

      {isLikedByModalVisible && (
        <LikedByModal
          isVisible={isLikedByModalVisible}
          onClose={() => setIsLikedByModalVisible(false)}
          pageId={article.page.id}
          articleId={article.id}
          likedByCount={article.likesCount}
        />
      )}
    </ArticleFooterWrapper>
  );
};
